/**
 * Pointer / keyboard → nearest bar on the price chart (F12).
 *
 * A component receives this hit and never recomputes the scale — the same
 * split as `chart-path.ts`. Session dates are payload facts (`PriceSeries.
 * sessions`), not geometry. Y comes from a caller-supplied `ChartRange`
 * (`ChartGeometry.range`); a non-finite bar is not a hit.
 */
import { CHART_WIDTH } from '../constants/chart';

import { chartXAt, chartYAt, type ChartRange } from './chart-path';

export interface ChartHit {
  readonly index: number;
  readonly pointCount: number;
  readonly price: number;
  readonly prev: number | null;
  readonly x: number;
  readonly y: number;
}

function indexAt(viewBoxX: number, count: number): number {
  if (count <= 1) return 0;
  const t = viewBoxX / CHART_WIDTH;
  return Math.round(Math.min(1, Math.max(0, t)) * (count - 1));
}

function packHit(
  bars: readonly number[],
  index: number,
  x: number,
  range: ChartRange,
): ChartHit | null {
  const price = bars[index];
  if (price === undefined || !Number.isFinite(price)) return null;
  const rawPrev = index === 0 ? undefined : bars[index - 1];
  const prev = rawPrev !== undefined && Number.isFinite(rawPrev) ? rawPrev : null;
  return {
    index,
    pointCount: bars.length,
    price,
    prev,
    x,
    y: chartYAt(price, range),
  };
}

/**
 * Map a pointer's client X through the SVG's laid-out box onto viewBox x.
 * A zero-width box (unstaged layout) is not a position.
 */
export function viewBoxXFromClient(
  clientX: number,
  rectLeft: number,
  rectWidth: number,
): number | null {
  if (!(rectWidth > 0) || !Number.isFinite(clientX) || !Number.isFinite(rectLeft)) {
    return null;
  }
  const x = ((clientX - rectLeft) / rectWidth) * CHART_WIDTH;
  if (!Number.isFinite(x)) return null;
  return Math.min(CHART_WIDTH, Math.max(0, x));
}

/**
 * Nearest bar under a viewBox x. `range` is the full-series high/low
 * (`chartScale` / `ChartGeometry.range`); this function does not rescan.
 */
export function hitTestChart(
  bars: readonly number[],
  viewBoxX: number,
  range: ChartRange,
): ChartHit | null {
  const n = bars.length;
  if (n === 0 || !Number.isFinite(viewBoxX)) return null;
  const clampedX = Math.min(CHART_WIDTH, Math.max(0, viewBoxX));
  const index = indexAt(clampedX, n);
  return packHit(bars, index, n === 1 ? clampedX : chartXAt(index, n), range);
}

/**
 * Bar at an explicit index. Keyboard scrubbing uses this so it does not have
 * to invent a pointer x; a single bar sits at mid-width rather than x = 0.
 */
export function hitTestChartIndex(
  bars: readonly number[],
  index: number,
  range: ChartRange,
): ChartHit | null {
  if (!Number.isInteger(index)) return null;
  if (index < 0 || index >= bars.length) return null;
  const n = bars.length;
  const x = n === 1 ? CHART_WIDTH / 2 : chartXAt(index, n);
  return packHit(bars, index, x, range);
}
